import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

export const Header = ({ isMobile }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Sample Reports', path: '/reports' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Trust Centre', path: '/trust-centre' },
  ];

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={scrolled ? 4 : 0}
        sx={{
          backgroundColor: scrolled ? 'rgba(10, 61, 98, 0.98)' : '#0A3D62',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease-in-out',
          boxShadow: scrolled 
            ? '0 4px 20px rgba(0, 0, 0, 0.15)' 
            : 'none',
        }}
      >
        <Toolbar 
          sx={{ 
            justifyContent: 'space-between', 
            padding: isMobile ? '12px 16px' : '0 40px',
            minHeight: { xs: '72px', md: '80px' },
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src="/c-encode-white.png"
            alt="C-Encode Logo"
            onClick={() => window.location.href = '/'}
            sx={{
              height: { xs: '48px', sm: '52px', md: '56px' },
              width: 'auto',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
          
          {/* Desktop Navigation */}
          {!isSmallScreen && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  href={item.path}
                  sx={{
                    color: '#FFFFFF',
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: isActive(item.path) ? 600 : 400,
                    padding: '8px 16px',
                    borderRadius: '8px',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '4px',
                      left: '50%',
                      transform: isActive(item.path) ? 'translateX(-50%)' : 'translateX(-50%) scaleX(0)',
                      width: '60%',
                      height: '2px',
                      backgroundColor: '#FF6B35',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '&::after': {
                        transform: 'translateX(-50%) scaleX(1)',
                      },
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                href="https://calendly.com/c-encode/demo"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                  color: '#FFFFFF',
                  marginLeft: '16px',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #E55A2B 0%, #D04A1B 100%)',
                    boxShadow: '0 6px 20px rgba(255, 107, 53, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Book a Demo
              </Button>
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isSmallScreen && (
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMenuToggle}
              sx={{
                marginLeft: 'auto',
                padding: '8px',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'rotate(90deg)',
                },
              }}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={handleMenuToggle}
        PaperProps={{
          sx: {
            width: { xs: '280px', sm: '320px' },
            backgroundColor: '#0A3D62',
            color: '#FFFFFF',
          },
        }}
      >
        <Box sx={{ padding: '20px 0' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '0 20px 20px 20px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <Box
              component="img"
              src="/c-encode-white.png"
              alt="C-Encode Logo"
              sx={{
                height: '44px',
                width: 'auto',
              }}
            />
            <IconButton
              onClick={handleMenuToggle}
              sx={{
                color: '#FFFFFF',
                padding: '8px',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <List sx={{ padding: '16px 0' }}>
            {navItems.map((item, index) => (
              <React.Fragment key={item.path}>
                <ListItem disablePadding>
                  <ListItemButton
                    href={item.path}
                    onClick={handleMenuToggle}
                    sx={{
                      padding: '16px 24px',
                      backgroundColor: isActive(item.path) ? 'rgba(255, 107, 53, 0.15)' : 'transparent',
                      borderLeft: isActive(item.path) ? '3px solid #FF6B35' : '3px solid transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderLeft: '3px solid #FF6B35',
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '1rem',
                        fontWeight: isActive(item.path) ? 600 : 400,
                        color: '#FFFFFF',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                {index < navItems.length - 1 && (
                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', margin: '4px 0' }} />
                )}
              </React.Fragment>
            ))}
          </List>

          <Box sx={{ padding: '20px 24px' }}>
            <Button
              fullWidth
              href="https://calendly.com/c-encode/demo"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              onClick={handleMenuToggle}
              sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
                color: '#FFFFFF',
                padding: '14px 24px',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #E55A2B 0%, #D04A1B 100%)',
                  boxShadow: '0 6px 20px rgba(255, 107, 53, 0.4)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Book a Demo
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Spacer to prevent content from going under fixed header */}
      <Toolbar sx={{ minHeight: { xs: '72px', md: '80px' } }} />
    </>
  );
};

export default Header;

